import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShieldCheck, Users, TreePine } from 'lucide-react';
import { siteMeta, contactInfo } from '../data';
import { Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';

export default function About() {
  const [heroImages, setHeroImages] = useState([
    "/_assets/th-skovservice-skoventreprenoer-maskine.jpg"
  ]);
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    fetch('/_api/public/content/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => {
        const allImages = data.flatMap(item => item.image || []);
        if (allImages.length > 0) {
          const shuffled = [...allImages].sort(() => Math.random() - 0.5);
          setHeroImages(shuffled);
        }
      })
      .catch(err => console.error('Error fetching gallery for hero:', err));
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroImages]);

  return (
    <>
      <Helmet>
        <title>Om os | {siteMeta.title}</title>
        <meta name="description" content="Læs mere om TH Skovservice og vores passion for professionel skovservice og træpleje." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-emerald-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <AnimatePresence mode="wait">
            {heroImages.length > 0 && (
              <motion.img
                key={heroImageIndex}
                src={imageUrl(heroImages[heroImageIndex], 'width=1920,quality=85,format=auto')}
                alt="TH Skovservice - Passion for skovservice og træfældning på Mors og i Thy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mt-8"
          >
            <h1 className="text-[2rem] md:text-6xl font-bold mb-6 tracking-tight">Om TH Skovservice</h1>
            <p className="text-base md:text-xl text-emerald-100 leading-relaxed">
              Vi er din dedikerede partner i Nordjylland, når det kommer til professionel træpleje og skovservice. Med fokus på håndværk, sikkerhed og natur.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={imageUrl("/_assets/th-skovservice-maskinpark-arbejde.jpg", 'width=800,quality=85,format=auto')} 
                alt="TH Skovservice moderne maskinpark og udstyr til skovarbejde og træpleje" 
                className="rounded-none shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900">Passion for faget og respekt for naturen</h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                TH Skovservice blev grundlagt med en vision om at levere skovservice af højeste kvalitet. Vi kombinerer moderne maskinkraft med klassisk håndværk for at sikre de bedste resultater for vores kunder.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Uanset om det drejer sig om en vanskelig topkapning i en villahave eller større skovningsopgaver, går vi til opgaven med samme professionalisme og omhu. Vi ved, at hvert træ og hver have er unik, og vi rådgiver altid ud fra den enkelte kundes ønsker og behov.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
                  <span className="font-bold text-stone-900">Certificeret & Forsikret</span>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-emerald-600 shrink-0" />
                  <span className="font-bold text-stone-900">Lokalt forankret</span>
                </div>
                <div className="flex items-start gap-3">
                  <TreePine className="w-6 h-6 text-emerald-600 shrink-0" />
                  <span className="font-bold text-stone-900">Naturpleje i fokus</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <span className="font-bold text-stone-900">Kvalitetsgaranti</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-stone-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Vores værdier</h2>
            <p className="text-stone-600">Det her er hvad du kan forvente, når du vælger TH Skovservice som din samarbejdspartner.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Troværdighed',
                desc: 'En aftale er en aftale. Vi kommer til tiden og udfører arbejdet som aftalt.',
                icon: ShieldCheck
              },
              {
                title: 'Sikkerhed',
                desc: 'Vi går aldrig på kompromis med sikkerheden – hverken for os selv eller dine omgivelser.',
                icon: CheckCircle2
              },
              {
                title: 'Effektivitet',
                desc: 'Med det rette udstyr og erfaring løser vi opgaverne hurtigt og professionelt.',
                icon: ArrowRight
              }
            ].map((value, i) => (
              <div key={i} className="bg-white p-10 rounded-none shadow-sm border border-stone-100 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-none flex items-center justify-center text-emerald-700 mx-auto mb-6">
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                <p className="text-stone-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-emerald-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">Skal vi hjælpe dig med dit næste projekt?</h2>
          <Link 
            to="/kontakt" 
            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-emerald-900 rounded-none text-lg font-bold hover:bg-stone-100 transition-all shadow-xl"
          >
            Kontakt os i dag
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </>
  );
}
